import { useEffect, useMemo, useRef, useState } from 'react';
import type { HeatmapTableHandle } from '../HeatmapTable';
import { DashboardHeatmap } from '../heatmap/DashboardHeatmap';
import { getTimeSpentColumn } from '../heatmap/timeSpentColumn.utils';
import { HeatmapLoadingState } from '../HeatmapLoadingState';
import { Toolbar } from '../Toolbar';
import { DashboardRightRail } from '../dashboard/DashboardRightRail';
import { formatSessionDateLabel } from '../dashboard/dashboardFormat';
import { useAutoCenterController } from '../../hooks/useAutoCenterController';
import { useSessionAnalytics } from '../../hooks/useSessionAnalytics';
import { formatBrowserTabTitle } from '../../lib/browserTabTitle';
import { getDisplayExpirations, type HeatmapViewPreset } from '../../lib/heatmapViewPreset';
import { getDefaultSessionDate } from '../../lib/sessionDate';
import { getHeatmapKey, useHeatmapStore, type Ticker } from '../../store/heatmapStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import type { AuthRevalidationFn } from '../../lib/auth';
import type { SupportedSymbol } from '../../lib/symbols';
import type { SessionTimeframe } from '../../types/session';
import type { UserWatchlist } from '../../lib/watchlists';

interface HeatmapsViewProps {
  ticker: Ticker;
  symbols: readonly SupportedSymbol[];
  watchlists: readonly UserWatchlist[];
  activeWatchlistId: string;
  activeWatchlistSymbolCounts: ReadonlyMap<string, number>;
  showWatchlists: boolean;
  watchlistsLoaded: boolean;
  watchlistLoadError: string | null;
  heatmapViewPreset: HeatmapViewPreset;
  rightRailDashboardEnabled: boolean;
  onTickerChange: (ticker: Ticker) => void;
  onAuthRevalidationRequested: AuthRevalidationFn;
  onWatchlistChange: (watchlistId: string) => void;
  onManageWatchlists: () => void;
  onRetryWatchlists: () => void;
}

export function HeatmapsView({
  ticker,
  symbols,
  watchlists,
  activeWatchlistId,
  activeWatchlistSymbolCounts,
  showWatchlists,
  watchlistsLoaded,
  watchlistLoadError,
  heatmapViewPreset,
  rightRailDashboardEnabled,
  onTickerChange,
  onAuthRevalidationRequested,
  onWatchlistChange,
  onManageWatchlists,
  onRetryWatchlists,
}: HeatmapsViewProps) {
  const type = useWorkspaceStore((state) => state.activeType);
  const setType = useWorkspaceStore((state) => state.setActiveType);
  const [sessionDate, setSessionDate] = useState(getDefaultSessionDate);
  const [timeframe] = useState<SessionTimeframe>('1D');
  const activeHeatmapKey = getHeatmapKey(ticker, type);
  const selectedHeatmap = useHeatmapStore((state) => state.heatmaps[activeHeatmapKey] ?? null);
  const connectionStatus = useHeatmapStore((state) => state.connectionStatus);
  const bootstrapStatus = useHeatmapStore((state) => state.bootstrapStatus);
  const heatmapRef = useRef<HeatmapTableHandle>(null);
  const heatmapData = selectedHeatmap?.data ?? null;
  const visibleExpirations = useMemo(
    () =>
      heatmapData == null
        ? []
        : getDisplayExpirations(heatmapData.expirations, heatmapViewPreset.visibleExpirationCount),
    [heatmapData, heatmapViewPreset.visibleExpirationCount],
  );
  const emptyStateMessage =
    selectedHeatmap != null
      ? null
      : connectionStatus === 'retrying'
        ? `Reconnecting to live ${type.toUpperCase()} stream for ${ticker}...`
        : bootstrapStatus === 'failed'
          ? 'Unable to load cached heatmaps. Waiting for live updates...'
          : bootstrapStatus === 'complete'
            ? `No cached ${type.toUpperCase()} heatmap for ${ticker}. Waiting for live update...`
            : null;
  const isLoading = selectedHeatmap == null && emptyStateMessage == null;
  const { autoCenterMode, autoCenterDisabled, onCenterSpot, onCenterKing } =
    useAutoCenterController({
      activeViewKey: activeHeatmapKey,
      hasHeatmapData: heatmapData != null,
      heatmapRef,
    });
  const {
    analytics: sessionAnalytics,
    isLoading: sessionAnalyticsLoading,
    error: sessionAnalyticsError,
    retry: retrySessionAnalytics,
  } = useSessionAnalytics({
    ticker,
    type,
    sessionDate,
    timeframe,
    enabled: rightRailDashboardEnabled && heatmapData != null,
    onAuthRevalidationRequested,
  });
  const currentSessionAnalytics =
    sessionAnalytics != null &&
    sessionAnalytics.ticker === ticker &&
    sessionAnalytics.exposureType === type &&
    sessionAnalytics.sessionDate === sessionDate &&
    sessionAnalytics.timeframe === timeframe
      ? sessionAnalytics
      : null;
  const timeSpent = useMemo(
    () =>
      getTimeSpentColumn({
        enabled: rightRailDashboardEnabled,
        analytics: currentSessionAnalytics,
        error: sessionAnalyticsError,
      }),
    [rightRailDashboardEnabled, currentSessionAnalytics, sessionAnalyticsError],
  );

  useEffect(() => {
    document.title = formatBrowserTabTitle(ticker, heatmapData?.spotPrice ?? null);
  }, [heatmapData?.spotPrice, ticker]);

  return (
    <div
      className={`grid min-h-0 flex-1 grid-cols-1 leading-[normal] content-start gap-3 overflow-y-auto px-2 pt-2.5 min-[721px]:gap-2 min-[721px]:pt-2 fit:content-stretch fit:grid-rows-[minmax(0,1fr)] fit:overflow-hidden ${
        rightRailDashboardEnabled ? 'min-[1101px]:grid-cols-[minmax(0,7fr)_minmax(296px,3fr)]' : ''
      }`}
    >
      <main className="flex min-w-0 flex-col gap-2 fit:min-h-0">
        <nav aria-label="Heatmap controls">
          <Toolbar
            type={type}
            onTypeChange={setType}
            symbols={symbols}
            watchlists={watchlists}
            activeWatchlistId={activeWatchlistId}
            activeWatchlistSymbolCounts={activeWatchlistSymbolCounts}
            showWatchlists={showWatchlists}
            watchlistsLoaded={watchlistsLoaded}
            watchlistLoadError={watchlistLoadError}
            activeTicker={ticker}
            spotPrice={heatmapData?.spotPrice ?? null}
            autoCenterMode={autoCenterMode}
            autoCenterDisabled={autoCenterDisabled}
            sessionDate={rightRailDashboardEnabled ? sessionDate : undefined}
            onSessionDateChange={rightRailDashboardEnabled ? setSessionDate : undefined}
            onTickerChange={onTickerChange}
            onWatchlistChange={onWatchlistChange}
            onManageWatchlists={onManageWatchlists}
            onRetryWatchlists={onRetryWatchlists}
            onCenterSpot={onCenterSpot}
            onCenterKing={onCenterKing}
          />
        </nav>

        {heatmapData != null ? (
          <DashboardHeatmap
            ref={heatmapRef}
            rows={heatmapData.rows}
            expirations={visibleExpirations}
            spotPrice={heatmapData.spotPrice}
            visibleStrikeCount={heatmapViewPreset.visibleStrikeCount}
            type={type}
            ticker={ticker}
            timeSpent={timeSpent}
            timeSpentLabel={formatSessionDateLabel(sessionDate)}
          />
        ) : (
          <div className="flex min-h-[320px] flex-col rounded-md border border-line bg-panel fit:min-h-0 fit:flex-1">
            {isLoading ? (
              <HeatmapLoadingState message={`Loading ${type.toUpperCase()} for ${ticker}...`} />
            ) : (
              <div className="flex flex-1 items-center justify-center px-4 text-center">
                <span className="font-mono text-xs text-dim">{emptyStateMessage}</span>
              </div>
            )}
          </div>
        )}

        <footer className="flex flex-col gap-1 px-1 pb-2 text-[11px] text-dim min-[721px]:flex-row min-[721px]:justify-between">
          <span>Data is for informational purposes only and not financial advice.</span>
        </footer>
      </main>

      {rightRailDashboardEnabled && (
        <div className="flex min-w-0 flex-col fit:min-h-0 fit:overflow-y-auto fit:pb-2 fit:[overscroll-behavior:contain] fit:[scrollbar-width:thin]">
          <DashboardRightRail
            analytics={currentSessionAnalytics}
            ticker={ticker}
            sessionDate={sessionDate}
            isLoading={sessionAnalyticsLoading || heatmapData == null}
            error={sessionAnalyticsError}
            onRetry={retrySessionAnalytics}
          />
        </div>
      )}
    </div>
  );
}
